import {
  BasketIcon,
  CalendarBlankIcon,
  CreditCardIcon,
  NoteIcon,
  TrashIcon,
  WalletIcon,
} from '@phosphor-icons/react'
import { formatMoney } from '@paybae/shared'
import { useState } from 'react'

import { Comprobante } from '@/components/Comprobante'
import { FilaDetalle, HojaInferior } from '@/components/HojaInferior'
import { Money } from '@/components/Money'
import { PersonAvatar } from '@/components/PersonAvatar'
import { ApiError, nombreDe } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { useBorrarGasto, useTransaction } from '@/lib/queries'

/**
 * Todo lo que se sabe de un movimiento.
 *
 * Existe porque la lista de movimientos solo puede enseñar tres datos por
 * renglón —cuánto, qué y cuándo— y las preguntas que uno se hace después son
 * otras: quién lo hizo, con qué se pagó, si hubo comprobante. Sin un sitio
 * donde mirarlas, la respuesta era abrir el banco.
 *
 * Se abre como hoja y no como pantalla: se consulta y se cierra, y volver a la
 * lista tiene que dejarla donde estaba, no rehacer el desplazamiento.
 */
export function DetalleMovimiento({
  id,
  onCerrar,
}: {
  id: string | null
  onCerrar: () => void
}) {
  const { data, isPending, isError } = useTransaction(id)
  const borrar = useBorrarGasto()
  const [confirmando, setConfirmando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const eliminar = async (): Promise<void> => {
    if (!id) return
    setError(null)
    try {
      await borrar.mutateAsync(id)
      haptic('success')
      onCerrar()
    } catch (cause) {
      haptic('error')
      setError(cause instanceof ApiError ? cause.message : 'No pudimos borrarlo.')
    }
  }

  return (
    <HojaInferior
      abierta={id !== null}
      titulo={data?.description || 'Movimiento'}
      descripcion={data ? fechaLarga(data.occurredOn) : undefined}
      onCerrar={() => {
        setConfirmando(false)
        setError(null)
        onCerrar()
      }}
    >
      {id === null ? null : isPending ? (
        <Cargando />
      ) : isError || !data ? (
        <p className="rounded-card bg-surface px-3.5 py-6 text-center text-[13px] text-ink-soft">
          No pudimos cargar este movimiento. Cierra y vuelve a intentarlo.
        </p>
      ) : (
        <>
          {/* El monto arriba y grande: es lo que se viene a comprobar. */}
          <div className="flex flex-col items-center gap-1 rounded-card bg-surface px-4 py-5">
            <span
              data-money
              className={cn(
                'font-display text-[32px] font-extrabold leading-none tracking-[-0.04em]',
                data.type === 'INCOME' ? 'text-income-text' : 'text-ink',
              )}
            >
              {formatMoney(data.amountCents, { hideZeroCents: false })}
            </span>
            {data.category ? (
              <span className="text-[13px] text-ink-soft">{data.category.name}</span>
            ) : null}
          </div>

          <Encabezado>De quién fue</Encabezado>
          <FilaDetalle
            titulo={
              data.attributedTo ? nombreDe(data.attributedTo) : 'Del fondo común'
            }
            detalle={
              data.attributedTo
                ? 'Se descuenta de su dinero'
                : 'Se descuenta del dinero de todos'
            }
            monto={
              data.attributedTo ? (
                <PersonAvatar
                  name={nombreDe(data.attributedTo)}
                  token={data.attributedTo.personColorToken}
                  size="sm"
                />
              ) : null
            }
          />
          {/* Quién lo capturó, solo cuando no es la misma persona: si coinciden,
              el renglón repite lo de arriba y no aporta nada. */}
          {data.createdBy && data.createdBy.id !== data.attributedTo?.id ? (
            <FilaDetalle
              titulo={`Lo registró ${nombreDe(data.createdBy)}`}
              monto={
                <PersonAvatar
                  name={nombreDe(data.createdBy)}
                  token={data.createdBy.personColorToken}
                  size="sm"
                />
              }
            />
          ) : null}

          <Encabezado>Con qué se pagó</Encabezado>
          {(data.payments ?? []).length > 0 ? (
            (data.payments ?? []).map((parte) => (
              <FilaDetalle
                key={parte.id}
                titulo={
                  parte.creditCard
                    ? `${parte.creditCard.name}${parte.creditCard.last4 ? ` ·· ${parte.creditCard.last4}` : ''}`
                    : (parte.account?.name ?? 'Efectivo')
                }
                detalle={
                  parte.creditCard
                    ? 'Crédito · se descuenta al vencer el corte'
                    : parte.account?.type === 'MEAL_VOUCHER'
                      ? 'Vales de despensa'
                      : 'Débito o efectivo'
                }
                color={
                  parte.creditCard
                    ? 'var(--expense)'
                    : parte.account?.type === 'MEAL_VOUCHER'
                      ? 'var(--warning)'
                      : 'var(--primary)'
                }
                monto={<Money cents={parte.amountCents} />}
              />
            ))
          ) : (
            <FilaDetalle
              titulo={data.creditCard?.name ?? data.account?.name ?? 'Sin método'}
              monto={<Money cents={data.amountCents} />}
            />
          )}
          {(data.payments ?? []).length > 1 ? (
            <p className="px-1 text-[12px] leading-snug text-ink-faint">
              Pago combinado: se repartió entre {(data.payments ?? []).length} métodos.
            </p>
          ) : null}

          {data.notes ? (
            <>
              <Encabezado>Notas</Encabezado>
              <p className="flex items-start gap-2 rounded-card bg-surface px-3.5 py-3 text-[13px] leading-snug text-ink-soft">
                <NoteIcon size={15} className="mt-0.5 shrink-0 text-ink-faint" />
                {data.notes}
              </p>
            </>
          ) : null}

          {/* El comprobante: se puede añadir aquí y no solo al capturar. La foto
              del ticket suele aparecer después —en la bolsa, al llegar a casa—,
              y si el único momento de adjuntarla es la captura, no se adjunta. */}
          <Encabezado>Comprobante</Encabezado>
          <Comprobante destino={{ transactionId: data.id }} adjuntos={data.files ?? []} />

          <Encabezado>Detalles</Encabezado>
          <FilaDetalle
            titulo="Fecha"
            monto={<span className="font-mono text-[13px] text-ink">{data.occurredOn}</span>}
            color="var(--ink-faint)"
          />
          {data.merchant ? <FilaDetalle titulo="Comercio" monto={data.merchant} /> : null}
          {data.cardStatement ? (
            <FilaDetalle
              titulo="Cae en el corte"
              detalle={`Se paga el ${data.cardStatement.dueDate}`}
              monto={
                <span className="font-mono text-[13px] text-ink">
                  {data.cardStatement.cutoffDate}
                </span>
              }
            />
          ) : null}

          {error ? (
            <p role="alert" className="text-[12px] text-expense-text">
              {error}
            </p>
          ) : null}

          {/* Borrar va al final y con confirmación: deshacer un movimiento
              devuelve saldos, y hacerlo por accidente descuadra la cuenta. */}
          {!confirmando ? (
            <button
              type="button"
              onClick={() => {
                haptic('light')
                setConfirmando(true)
              }}
              className="mt-2 flex min-h-[48px] items-center justify-center gap-2 rounded-card text-[14px] font-semibold text-expense-text active:scale-[0.99]"
            >
              <TrashIcon size={16} />
              Borrar este movimiento
            </button>
          ) : (
            <div className="mt-2 flex flex-col gap-2 rounded-card bg-expense-soft p-3">
              <p className="text-[13px] leading-snug text-expense-text">
                Al borrarlo se devuelve el dinero a la cuenta o a la tarjeta de donde salió.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmando(false)}
                  className="min-h-[48px] flex-1 rounded-card border border-line bg-surface text-[14px] font-semibold text-ink active:scale-[0.99]"
                >
                  Mejor no
                </button>
                <button
                  type="button"
                  onClick={() => void eliminar()}
                  disabled={borrar.isPending}
                  className="min-h-[48px] flex-1 rounded-card bg-expense text-[14px] font-semibold text-white active:scale-[0.99] disabled:opacity-70"
                >
                  {borrar.isPending ? 'Borrando…' : 'Sí, borrarlo'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </HojaInferior>
  )
}

function Encabezado({ children }: { children: string }) {
  return (
    <h3 className="mt-2 px-1 text-[12px] font-semibold uppercase tracking-[0.07em] text-ink-faint">
      {children}
    </h3>
  )
}

function Cargando() {
  return (
    <div className="flex flex-col gap-2" aria-busy>
      <div className="h-[92px] animate-pulse rounded-card bg-surface-sunken" />
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-[52px] animate-pulse rounded-card bg-surface-sunken" />
      ))}
    </div>
  )
}

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
]

function fechaLarga(fecha: string): string {
  const [, mes, dia] = fecha.split('-')
  return `${Number(dia)} de ${MESES[Number(mes) - 1]}`
}

/** Iconos por método, para reusar donde haga falta. */
export const ICONO_METODO = {
  cuenta: WalletIcon,
  tarjeta: CreditCardIcon,
  vales: BasketIcon,
  fecha: CalendarBlankIcon,
}
